import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ImagePlus, Save, Send, X, Video, Images, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, resolveImage, CATEGORIES } from "@/lib/api";
import { RichTextEditor } from "@/components/RichTextEditor";
import { VideoPlayer } from "@/components/VideoPlayer";
import { MediaDropZone } from "@/components/MediaDropZone";

const empty = { title: "", excerpt: "", content: "", category: "story", state: "", tags: [], cover_image: "", video_url: "", gallery: [], title_hi: "", excerpt_hi: "", content_hi: "", published: false };

export default function AdminEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [tagInput, setTagInput] = useState("");
  const [galleryUrl, setGalleryUrl] = useState("");
  const [editLang, setEditLang] = useState("en");
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(null);
  const [galleryBusy, setGalleryBusy] = useState(0);
  const coverRef = useRef(null);
  const videoRef = useRef(null);
  const galleryRef = useRef(null);

  useEffect(() => {
    if (!id) return;
    api.get(`/admin/articles/${id}`)
      .then((r) => setForm({ ...r.data, state: r.data.state || "", cover_image: r.data.cover_image || "", video_url: r.data.video_url || "", gallery: r.data.gallery || [], title_hi: r.data.title_hi || "", excerpt_hi: r.data.excerpt_hi || "", content_hi: r.data.content_hi || "" }))
      .catch((e) => { toast.error(apiError(e)); navigate("/admin"); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadCover = async (files) => {
    const file = files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd);
      set("cover_image", data.url);
      toast.success("Cover image added");
    } catch (err) {
      toast.error(apiError(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const uploadVideo = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) return toast.error("Video must be under 200 MB");
    const fd = new FormData();
    fd.append("file", file);
    setVideoProgress(0);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        timeout: 0,
        onUploadProgress: (ev) => setVideoProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0),
      });
      set("video_url", data.url);
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(apiError(err, "Video upload failed"));
    } finally {
      setVideoProgress(null);
    }
  };

  const uploadGallery = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setGalleryBusy(files.length);
    const added = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/admin/upload", fd);
        added.push(data.url);
      } catch (err) {
        toast.error(`${file.name}: ${apiError(err, "upload failed")}`);
      } finally {
        setGalleryBusy((n) => n - 1);
      }
    }
    if (added.length) {
      setForm((f) => ({ ...f, gallery: [...f.gallery, ...added] }));
      toast.success(`${added.length} photo${added.length === 1 ? "" : "s"} added to gallery`);
    }
  };

  const moveGallery = (i, dir) => {
    setForm((f) => {
      const g = [...f.gallery];
      const j = i + dir;
      if (j < 0 || j >= g.length) return f;
      [g[i], g[j]] = [g[j], g[i]];
      return { ...f, gallery: g };
    });
  };

  const addTag = () => {
    const t = tagInput.trim().replace(/^#/, "");
    if (t && !form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  };

  const save = async (publish) => {
    if (form.title.trim().length < 3) return toast.error("Title needs at least 3 characters");
    setSaving(true);
    const emptyHtml = (h) => !h || /^<p>\s*<\/p>$/.test(h.trim());
    const payload = {
      ...form, state: form.state || null, cover_image: form.cover_image || null, video_url: form.video_url || null, published: publish,
      title_hi: form.title_hi?.trim() || null, excerpt_hi: form.excerpt_hi?.trim() || null, content_hi: emptyHtml(form.content_hi) ? null : form.content_hi,
    };
    delete payload.audio_url_hi; delete payload.audio_hash_hi;
    delete payload.id; delete payload.slug; delete payload.author; delete payload.read_time;
    delete payload.created_at; delete payload.updated_at; delete payload.published_at;
    delete payload.notified_at; delete payload.notified_count; delete payload.audio_url; delete payload.audio_hash;
    try {
      if (id) await api.put(`/admin/articles/${id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success(publish ? "Article published" : "Draft saved");
      navigate("/admin");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center text-stone-500">Loading article...</div>;

  const inputCls = "mt-1.5 w-full rounded-xl border border-[var(--line)] bg-white px-4 py-2.5 text-base outline-none transition-colors focus:border-[var(--chakra)] focus:ring-2 focus:ring-[var(--chakra-tint)]";

  return (
    <div className="relative z-10 min-h-screen" data-testid="admin-editor">
      <header className="sticky top-0 z-30 border-b border-[var(--line)] tricolor-light-h backdrop-blur">
        <div className="tricolor-bar h-1" />
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-3 sm:px-8">
          <Link to="/admin" data-testid="editor-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-stone-600 hover:text-[var(--chakra)]"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
          <div className="flex items-center gap-2">
            <button onClick={() => save(false)} disabled={saving} data-testid="editor-save-draft-btn" className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-white px-4 py-2 text-sm font-semibold text-stone-700 transition-colors hover:border-[var(--chakra)] disabled:opacity-60">
              <Save className="h-4 w-4" /> Save draft
            </button>
            <button onClick={() => save(true)} disabled={saving} data-testid="editor-publish-btn" className="inline-flex items-center gap-2 rounded-full bg-[var(--india-green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--india-green-deep)] disabled:opacity-60">
              <Send className="h-4 w-4" /> {form.published && id ? "Update" : "Publish"}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl gap-8 px-5 py-10 sm:px-8 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          <div className="flex flex-wrap items-center justify-between gap-3" data-testid="editor-lang-tabs">
            <div className="inline-flex rounded-full border border-[var(--line)] bg-white p-1 text-sm font-semibold">
              <button onClick={() => setEditLang("en")} data-testid="editor-lang-en" className={`rounded-full px-4 py-1.5 transition-colors ${editLang === "en" ? "bg-[var(--chakra)] text-white" : "text-stone-600"}`}>English</button>
              <button onClick={() => setEditLang("hi")} data-testid="editor-lang-hi" className={`rounded-full px-4 py-1.5 font-devanagari text-base leading-none transition-colors ${editLang === "hi" ? "bg-[var(--saffron)] text-white" : "text-stone-600"}`}>
                हिन्दी {form.content_hi ? <span className="ml-1 text-xs">✓</span> : null}
              </button>
            </div>
            <p className="text-xs text-stone-500">
              {editLang === "hi" ? "Hindi version: readers get a one-tap हिन्दी switch when this is filled in." : "English is the main version. Switch to हिन्दी to add a translation."}
            </p>
          </div>
          {editLang === "en" ? (
            <>
              <input
                data-testid="editor-title-input"
                value={form.title} onChange={(e) => set("title", e.target.value)}
                placeholder="Article title"
                className="w-full border-0 border-b-2 border-[var(--line)] bg-transparent pb-3 font-serif text-3xl font-bold text-[var(--chakra-deep)] outline-none placeholder:text-stone-300 focus:border-[var(--saffron)] sm:text-4xl"
              />
              <textarea
                data-testid="editor-excerpt-input"
                value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)}
                placeholder="A short summary that appears on cards and at the top of the article"
                rows={2} maxLength={500}
                className="w-full resize-none rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base text-stone-700 outline-none focus:border-[var(--chakra)]"
              />
              <RichTextEditor key="en" value={form.content} onChange={(html) => set("content", html)} />
            </>
          ) : (
            <>
              <input
                data-testid="editor-title-hi-input"
                value={form.title_hi} onChange={(e) => set("title_hi", e.target.value)}
                placeholder="लेख का शीर्षक (Hindi title)"
                lang="hi"
                className="w-full border-0 border-b-2 border-[var(--line)] bg-transparent pb-3 font-devanagari text-3xl text-[var(--chakra-deep)] outline-none placeholder:text-stone-300 focus:border-[var(--saffron)] sm:text-4xl"
              />
              <textarea
                data-testid="editor-excerpt-hi-input"
                value={form.excerpt_hi} onChange={(e) => set("excerpt_hi", e.target.value)}
                placeholder="संक्षिप्त सारांश (Hindi summary)"
                rows={2} maxLength={500} lang="hi"
                className="w-full resize-none rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base text-stone-700 outline-none focus:border-[var(--chakra)]"
              />
              <RichTextEditor key="hi" value={form.content_hi} onChange={(html) => set("content_hi", html)} placeholder="यहाँ हिन्दी में लेख लिखें..." />
              {form.content_hi && (
                <button onClick={() => { if (window.confirm("Remove the Hindi version?")) setForm((f) => ({ ...f, title_hi: "", excerpt_hi: "", content_hi: "" })); }} data-testid="editor-remove-hindi-btn" className="text-sm text-red-600 underline">Remove Hindi version</button>
              )}
            </>
          )}
        </div>

        <aside className="space-y-6 lg:col-span-4">
          <div className="rounded-2xl border border-[var(--line)] bg-white p-5" onPaste={(e) => { if (e.clipboardData?.files?.length && !form.cover_image) { e.preventDefault(); uploadCover(Array.from(e.clipboardData.files).filter((f) => f.type.startsWith("image/"))); } }}>
            <p className="eyebrow text-[var(--saffron-deep)]">Cover image</p>
            <div className="mt-3" data-testid="cover-preview">
              {form.cover_image ? (
                <div className="relative overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)]">
                  <img src={resolveImage(form.cover_image)} alt="Cover" className="max-h-72 w-full object-contain" />
                  <button onClick={() => set("cover_image", "")} data-testid="cover-remove-btn" className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-stone-700 shadow hover:text-red-600" aria-label="Remove cover"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <MediaDropZone testid="cover-dropzone" accept="image/*" inputRef={coverRef} onFiles={uploadCover} busy={uploading} icon={ImagePlus} label="Upload, drop or paste a picture" className="aspect-video" />
              )}
            </div>
            {form.cover_image && (
              <button onClick={() => coverRef.current?.click()} disabled={uploading} data-testid="cover-replace-btn" className="mt-3 text-sm font-medium text-[var(--chakra)] underline">{uploading ? "Uploading..." : "Replace image"}</button>
            )}
            {form.cover_image && <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadCover(e.target.files); e.target.value = ""; }} data-testid="cover-file-input" />}
            <input data-testid="cover-url-input" value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="or paste an image URL / link" className={`${inputCls} text-sm`} />
          </div>

          <div className="rounded-2xl border border-[var(--line)] bg-white p-5" data-testid="video-card">
            <p className="eyebrow text-[var(--india-green)]">Video (optional)</p>
            <div className="mt-3" data-testid="video-preview">
              {form.video_url ? (
                <div className="relative overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)]">
                  <VideoPlayer url={form.video_url} title="Video preview" className="!rounded-none !shadow-none" />
                  <button onClick={() => set("video_url", "")} data-testid="video-remove-btn" className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-stone-700 shadow hover:text-red-600" aria-label="Remove video"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <MediaDropZone testid="video-dropzone" accept="video/mp4,video/webm,video/quicktime,video/ogg" inputRef={videoRef} onFiles={uploadVideo} busy={videoProgress !== null} busyLabel={`Uploading… ${videoProgress ?? 0}%`} icon={Video} label="Upload or drop a video (MP4, WEBM, MOV · up to 200 MB)" hint="Click to choose · or drag & drop a video file here" className="aspect-video" />
              )}
            </div>
            {videoProgress !== null && (
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[var(--line)]" data-testid="video-progress">
                <div className="h-full bg-[var(--india-green)] transition-[width]" style={{ width: `${videoProgress}%` }} />
              </div>
            )}
            {form.video_url && <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/ogg" className="hidden" onChange={(e) => { uploadVideo(e.target.files); e.target.value = ""; }} data-testid="video-file-input" />}
            {form.video_url && (
              <button onClick={() => videoRef.current?.click()} disabled={videoProgress !== null} data-testid="video-replace-btn" className="mt-3 text-sm font-medium text-[var(--chakra)] underline">Replace video</button>
            )}
            <input data-testid="video-url-input" value={form.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="or paste a YouTube / video URL" className={`${inputCls} text-sm`} />
          </div>

          <div className="rounded-2xl border border-[var(--line)] bg-white p-5" data-testid="gallery-card">
            <div className="flex items-center justify-between">
              <p className="eyebrow text-[var(--chakra)]">Photo gallery</p>
              <span className="text-xs text-stone-500" data-testid="gallery-count">{form.gallery.length} photo{form.gallery.length === 1 ? "" : "s"}</span>
            </div>
            {form.gallery.length > 0 && (
              <ul className="mt-3 grid grid-cols-3 gap-2" data-testid="gallery-thumbs-admin">
                {form.gallery.map((src, i) => (
                  <li key={src + i} className="group relative aspect-square overflow-hidden rounded-lg border border-[var(--line)] bg-[var(--paper)]" data-testid={`gallery-item-${i}`}>
                    <img src={resolveImage(src)} alt="" className="h-full w-full object-cover" />
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/50 px-1 py-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                      <button onClick={() => moveGallery(i, -1)} aria-label="Move left" data-testid={`gallery-move-left-${i}`} className="rounded p-0.5 text-white hover:bg-white/20"><ChevronLeft className="h-3.5 w-3.5" /></button>
                      <button onClick={() => set("gallery", form.gallery.filter((_, j) => j !== i))} aria-label="Remove photo" data-testid={`gallery-remove-${i}`} className="rounded p-0.5 text-white hover:bg-red-500"><X className="h-3.5 w-3.5" /></button>
                      <button onClick={() => moveGallery(i, 1)} aria-label="Move right" data-testid={`gallery-move-right-${i}`} className="rounded p-0.5 text-white hover:bg-white/20"><ChevronRight className="h-3.5 w-3.5" /></button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <MediaDropZone testid="gallery-dropzone" accept="image/*" multiple inputRef={galleryRef} onFiles={uploadGallery} busy={galleryBusy > 0} busyLabel={`Uploading ${galleryBusy} photo${galleryBusy === 1 ? "" : "s"}…`} icon={Images} label="Add photos: upload several, drop or paste" className="mt-3 py-5" />
            <form onSubmit={(e) => { e.preventDefault(); const u = galleryUrl.trim(); if (u) { set("gallery", [...form.gallery, u]); setGalleryUrl(""); } }} className="mt-3 flex gap-2">
              <input data-testid="gallery-url-input" value={galleryUrl} onChange={(e) => setGalleryUrl(e.target.value)} placeholder="or paste an image URL" className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-2 text-sm outline-none focus:border-[var(--chakra)]" />
              <button type="submit" data-testid="gallery-url-add-btn" className="rounded-xl bg-[var(--chakra)] px-4 text-sm font-semibold text-white">Add</button>
            </form>
            <p className="mt-2 text-xs text-stone-500">Readers can swipe through these below the article.</p>
          </div>

          <div className="rounded-2xl border border-[var(--line)] bg-white p-5">
            <p className="eyebrow text-[var(--chakra)]">Details</p>
            <label className="mt-3 block text-sm font-medium text-stone-700">
              Category
              <select data-testid="editor-category-select" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label} · {c.hindi}</option>)}
              </select>
            </label>
            <label className="mt-4 block text-sm font-medium text-stone-700">
              State / Region (optional)
              <input data-testid="editor-state-input" value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="e.g. Kerala" className={inputCls} />
            </label>
            <label className="mt-4 block text-sm font-medium text-stone-700">
              Tags
              <div className="mt-1.5 flex gap-2">
                <input data-testid="editor-tag-input" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder="Add a tag, press Enter" className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-2.5 text-base outline-none focus:border-[var(--chakra)]" />
                <button type="button" onClick={addTag} data-testid="editor-tag-add-btn" className="rounded-xl bg-[var(--chakra)] px-4 text-sm font-semibold text-white">Add</button>
              </div>
            </label>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="editor-tags-list">
              {form.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[var(--saffron-tint)] px-3 py-1 text-xs font-medium text-[var(--saffron-deep)]">
                  #{t}
                  <button onClick={() => set("tags", form.tags.filter((x) => x !== t))} aria-label={`Remove ${t}`} data-testid={`editor-tag-remove-${t}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-[var(--line)] bg-[var(--green-tint)] p-5 text-sm text-stone-700">
            <p className="font-semibold text-[var(--india-green-deep)]">Status: {form.published ? "Published" : "Draft"}</p>
            <p className="mt-1">"Save draft" keeps it private. "Publish" makes it live for readers across India and emails every confirmed subscriber once.</p>
          </div>
        </aside>
      </main>
    </div>
  );
}
